<!-- @format -->

<template>
	<pc-card :id="item.code" :header="item.pcProps.isHiddenLabel ? '' : item.pcProps.label">
		<template v-if="showType === '0'">
			<pc-table
				v-if="item.pcProps.actionButton.buttons"
				ref="subTableRef"
				:data="(value && value.filter((item) => !item['$delete_filed_flag$'])) || []"
				:column-list="columnList"
				:action-btn-config="[]"
				class="pc-table-header-bg"
				border
				:multiple="true"
				:row-key="rowKey"
				@action-btns-action="[]"
			>
				<template #button-front>
					<div
						v-if="!formDisabled"
						:style="{
							'margin-right': '8px',
						}"
					>
						<template v-for="item in actionBtnConfig" :key="item.code">
							<el-button
								:type="item.style"
								:circle="item.circle"
								:icon="item.icon"
								:style="{ height: `${item.height}`, width: `${item.width}` }"
								@click="actionBtnsAction(item.code, () => {}, item)"
							>
								{{ item.label }}
							</el-button>
						</template>
					</div>
				</template>
				<template v-for="itm in columnList" :key="itm.code" #[`column-default-${itm.code}`]="scope">
					<widget-form-item
						v-model="scope.row[itm.pcProps.code!]"
						:type="itm.type"
						:pc-props="itm.pcProps"
						:disabled="getDisabled(itm, scope)"
					/>
				</template>
			</pc-table>
			<pc-table
				v-else
				ref="subTableRef"
				:data="value || []"
				:column-list="columnList"
				:action-btn-config="actionBtnConfig"
				class="pc-table-header-bg"
				border
				:multiple="true"
				:row-key="rowKey"
				@action-btns-action="formDisabled ? [] : actionBtnsAction"
			>
				<template v-for="itm in columnList" :key="itm.code" #[`column-default-${itm.code}`]="scope">
					<widget-form-item
						v-model="scope.row[itm.pcProps.code!]"
						:type="itm.type"
						:pc-props="itm.pcProps"
						:disabled="getDisabled(itm, scope)"
					/>
				</template>
			</pc-table>
		</template>
		<template v-else>
			<div
				v-if="!formDisabled"
				:style="{
					'margin-bottom': '10px',
				}"
			>
				<template v-for="item in actionBtnConfig" :key="item.code">
					<el-button
						:type="item.style"
						:circle="item.circle"
						:icon="item.icon"
						:style="{ height: `${item.height}`, width: `${item.width}` }"
						@click="actionBtnsAction(item.code, () => {}, item)"
					>
						{{ item.label }}
					</el-button>
				</template>
			</div>
			<div v-for="(_v, k) in value" v-show="!value[k]['$delete_filed_flag$']" :key="k" class="form-item-child">
				<el-checkbox-group v-if="!formDisabled" v-model="checkList">
					<el-checkbox :label="value[k][rowKey]" :value="value[k][rowKey]" class="checkbox" />
				</el-checkbox-group>
				<el-row :gutter="8">
					<template v-for="itm in columnList" :key="itm.code">
						<el-col v-show="!itm.pcProps.hidden" :span="+itm.pcProps.cols" class="form-render-col">
							<widget-form-item-index
								v-show="itm.type !== 'placeholder'"
								v-model="value[k][itm.pcProps.code!]"
								:type="itm.type"
								:pc-props="itm.pcProps"
								:prop="`${item.pcProps.code}[${k}]['${itm.pcProps.code}']`"
								:item="itm"
								:hidden="itm.pcProps.hidden"
								:delete-filed="value[k]['$delete_filed_flag$']"
							/>
						</el-col>
					</template>
				</el-row>
			</div>
		</template>
	</pc-card>
</template>

<script setup lang="ts">
	import { uuid } from '@power/utils';
	import WidgetFormItemIndex from '../../widget-form-item/index.vue';
	import WidgetFormItem from '../../widget-form-item/widget-form-item.vue';
	import Script from '../../../utils/script';
	import PcTable from '@/components/pc-table/pc-table.vue';
	import { PcCard, getDefaultValue } from '@/components';

	interface Props {
		modelValue: any;
		item: any;
		disabled?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {});

	const emit = defineEmits(['update:modelValue']);

	const scriptConfig: Script | undefined = inject('scriptConfig', undefined);
	const formDisabled = inject('formDisabled', ref(false));

	const setFieldsPcProps = inject('setFieldsPcProps', (_) => {});

	const subTableRef = ref<InstanceType<typeof PcTable>>();

	const value = computed({
		get() {
			return props.modelValue;
		},
		set(val) {
			emit('update:modelValue', val);
		},
	});
	const checkList = ref([]);
	const rowKey: any = computed(() => {
		const key = props.item.pcProps.rowKey!;
		return key.includes('.') ? key.split('.').pop() : key;
	});
	const subTableCode = computed(() => props.item.pcProps.subTableCode || '');
	const showType = computed(() => props.item.pcProps.showType || '0');

	const hiddenColumnCodeList = ref(new Set());
	const disabledCodeList = ref({
		col: new Set(),
		row: new Set(),
		other: new Set(),
	});

	const columnList = computed(() => {
		return props.item.children.reduce((arr: any, itm) => {
			return arr.concat(
				hiddenColumnCodeList.value.has(itm.pcProps.code)
					? []
					: {
							label: itm.pcProps.label!,
							code: itm.pcProps.code!,
							type: itm.type,
							pcProps: itm.pcProps,
					  },
			);
		}, []);
	});

	const actionBtnConfig = computed(() => {
		if (props.item?.pcProps?.actionButton?.buttons) {
			return props.item.pcProps.actionButton.buttons?.map((item) => ({
				value: item.code,
				...item,
				factory: true,
			}));
		} else {
			return props.item.pcProps.actionButton?.map((item) => ({
				label: item.label,
				value: item.code,
				code: item.code,
				script: item.script,
			}));
		}
	});

	const onAction = inject('onAction', async (_params, _resCallback, _rejCallback) => {
		return false;
	});

	const onActionItem = (item) => {
		onAction(
			item,
			() => {},
			() => {},
		);
	};

	const getDisabled = (col, scope) => {
		return (
			props.disabled ||
			disabledCodeList.value.col.has(col.pcProps.code) ||
			disabledCodeList.value.row.has(scope.row[rowKey.value]) ||
			disabledCodeList.value.other.has(`${col.pcProps.code},${scope.row[rowKey.value]}`)
		);
	};

	const onAdd = (formData = {}) => {
		const defaultValue = getDefaultValue(props.item.children);
		value.value.push({ ...defaultValue, [rowKey.value]: uuid(), ...formData });
	};

	const onEdit = (formData = {}) => {
		const idx = value.value.findIndex((itm) => itm[rowKey.value] === formData[rowKey.value]);
		if (idx !== -1) {
			value.value.splice(idx, 1, formData);
		} else {
			onAdd(formData);
		}
	};

	const onDelete = (key?: string) => {
		if (key) {
			value.value = value.value.filter((itm) => itm[rowKey.value] !== key);
		} else {
			if (showType.value === '1') {
				if (subTableCode.value) {
					//给value中存在checkRows的数据添加一个标识
					for (const itm of value.value) {
						if (checkList.value.some((it) => it === itm[rowKey.value])) {
							itm['$delete_filed_flag$'] = true;
						}
					}
				} else {
					value.value = value.value.filter((itm) => checkList.value.every((it) => it !== itm[rowKey.value]));
				}
			} else {
				const checkRows = subTableRef.value?.tableRef?.getSelectionRows();
				if (subTableCode.value) {
					//给value中存在checkRows的数据添加一个标识
					for (const itm of value.value) {
						if (checkRows.some((it) => it[rowKey.value] === itm[rowKey.value])) {
							itm['$delete_filed_flag$'] = true;
						}
					}
				} else {
					value.value = value.value.filter((itm) => checkRows.every((it) => it[rowKey.value] !== itm[rowKey.value]));
				}
			}
		}
	};

	const onOtherFunc = (item) => {
		if (item.script) {
			if (scriptConfig) {
				(scriptConfig as Script).runScript(`${props.item.code}_${item.code}`, 'onClickScript', item.script);
			}
		}
	};

	const actionBtnsAction = (code, callback, item) => {
		callback();
		if (item.factory) {
			switch (item.btnType) {
				case 'add':
					onActionItem({ ...item, subTableFunc: onAdd });
					break;
				case 'delete':
					onActionItem({ ...item, subTableFunc: onDelete });
					break;
				default:
					onActionItem(item);
					break;
			}
		} else {
			switch (code) {
				case 'add':
					onAdd();
					break;
				case 'delete':
					onDelete();
					break;
				default:
					onOtherFunc(item);
					break;
			}
		}
	};

	const hiddenSubTableColumn = (code) => hiddenColumnCodeList.value.add(code);

	const showSubTableColumn = (code) => hiddenColumnCodeList.value.delete(code);

	const disabledSubTableCol = (col, row) => {
		if (col && row) {
			disabledCodeList.value.other.add(`${col},${row}`);
		} else if (col) {
			disabledCodeList.value.col.add(col);
		} else if (row) {
			disabledCodeList.value.row.add(row);
		}
	};

	const enabledSubTableCol = (col, row) => {
		if (col && row) {
			disabledCodeList.value.other.delete(`${col},${row}`);
		} else if (col) {
			disabledCodeList.value.col.delete(col);
		} else if (row) {
			disabledCodeList.value.row.delete(row);
		}
	};

	onMounted(() => {
		if (!value.value) {
			value.value = [];
		}
		setFieldsPcProps({
			[props.item.pcProps.code!]: {
				getAllList: () => value.value,
				getCheckedList: () => subTableRef.value?.tableRef?.getSelectionRows(),
				updateListItem: onEdit,
				delListItem: onDelete,
				hiddenSubTableColumn,
				showSubTableColumn,
				disabledSubTableCol,
				enabledSubTableCol,
			},
		});
	});
</script>

<style scoped>
	.form-item-child {
		border: 1px dashed #ccc;
		border-radius: 1px;
		margin: 5px 2px;
		padding: 16px;
		position: relative;
		padding-bottom: 0px;

		.checkbox {
			position: absolute;
			left: 5px;
			top: -5px;

			&:deep(.el-checkbox__label) {
				display: none;
			}
		}
	}
</style>
